import React, {
  FC,
  PropsWithChildren,
  useContext,
  useState,
  createContext,
  useMemo,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';

import Toast from './toast';

interface AppContextInterface {
  open: (content: string | ReactNode | JSX.Element) => void;
  error: (content: string | ReactNode | JSX.Element) => void;
}

interface IToast {
  id: string;
  content?: string | ReactNode | JSX.Element;
  type?: string;
}

const ToastContext = createContext<AppContextInterface | null>(null);

interface IProps {
  children: string | ReactNode | JSX.Element;
}

const ToastProvider: FC<PropsWithChildren<IProps>> = ({ children }: IProps) => {
  const [toasts, setToasts] = useState<Array<IToast>>([]);
  const open = (content: string | ReactNode | JSX.Element, type?: string) =>
    setToasts((currentToasts: Array<IToast>) => [
      ...currentToasts,
      { id: uuidv4(), content, type },
    ]);

  const error = (content: string | ReactNode | JSX.Element) =>
    open(content, 'error');
  const close = (id: string) =>
    setToasts((currentToasts: Array<IToast>) =>
      currentToasts.filter((toast: IToast) => toast.id !== id),
    );

  const contextValue = useMemo(() => ({ open, error }), []);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {createPortal(
        <div className="left-[55%] top-[5vh] z-50   absolute -translate-x-[50%] -translate-y-[50%] ">
          {toasts.map((toast: IToast) => (
            <Toast
              key={toast.id}
              close={() => close(toast.id)}
              isError={toast.type === 'error'}
            >
              {toast.content}
            </Toast>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

const useToast = () => {
  const toast = useContext(ToastContext);
  if (toast == null) {
    throw new Error('useContext() called outside of a ToastProvider?'); // an alert is not placed because this is an error for the developer not the user
  }
  return toast;
};

export { ToastProvider, useToast };
